import { Link } from "wouter";
import {
  Waves,
  Dumbbell,
  Users,
  Trees,
  Zap,
  Droplets,
  Wifi,
  Lock,
  Utensils,
  Gamepad2,
  Baby,
  Zap as Lightning,
  Shield,
  Leaf,
  Smartphone,
} from "lucide-react";

const Amenities = () => {
  const recreationAmenities = [
    {
      icon: Waves,
      title: "Swimming Pool",
      description: "Main swimming pool with snorkeling fish tank view",
    },
    {
      icon: Waves,
      title: "Wave Pool",
      description: "Unique water feature for recreational swimming",
    },
    {
      icon: Dumbbell,
      title: "Gymnasium",
      description: "Well-equipped fitness center with modern equipment",
    },
    {
      icon: Users,
      title: "Clubhouse",
      description: "Spacious community center for gatherings and events",
    },
    {
      icon: Gamepad2,
      title: "Indoor Games Room",
      description: "Multi-purpose recreation area for indoor sports",
    },
    {
      icon: Baby,
      title: "Children's Play Area",
      description: "Dedicated outdoor play space for kids",
    },
    {
      icon: Trees,
      title: "Landscaped Gardens",
      description: "Well-maintained green spaces and parks",
    },
    {
      icon: Leaf,
      title: "Maze Garden",
      description: "Unique landscaping feature for exploration",
    },
    {
      icon: Trees,
      title: "Jogging Track",
      description: "Fitness pathway for running and walking",
    },
    {
      icon: Dumbbell,
      title: "Aerobics Room",
      description: "Dedicated fitness facility for group classes",
    },
    {
      icon: Gamepad2,
      title: "Squash Court",
      description: "Professional sports facility for squash players",
    },
    {
      icon: Gamepad2,
      title: "Basketball Post",
      description: "Outdoor sports facility for basketball",
    },
  ];

  const infrastructureAmenities = [
    {
      icon: Lock,
      title: "24/7 Security",
      description: "CCTV surveillance, gated access, security personnel",
    },
    {
      icon: Shield,
      title: "Video Door Phones",
      description: "Intercom facilities in all units for secure communication",
    },
    {
      icon: Lock,
      title: "Smart Card Access",
      description: "Access-controlled entry system for enhanced security",
    },
    {
      icon: Lightning,
      title: "Fire Safety Systems",
      description: "Fire detection, alarms, intruder sensors, emergency exits",
    },
    {
      icon: Zap,
      title: "100% Power Backup",
      description: "Backup for common areas + 3000W per apartment",
    },
    {
      icon: Droplets,
      title: "Water Management",
      description: "Reliable water supply system with management infrastructure",
    },
    {
      icon: Wifi,
      title: "High-Speed Internet",
      description: "Integrated connectivity infrastructure throughout community",
    },
    {
      icon: Smartphone,
      title: "Lifts & Elevators",
      description: "Hi-speed automatic lifts with intercom facility",
    },
    {
      icon: Utensils,
      title: "Laundry Service",
      description: "Professional laundry facilities available for residents",
    },
    {
      icon: Utensils,
      title: "Shops Within Society",
      description: "Retail convenience stores for daily needs",
    },
    {
      icon: Users,
      title: "Professional Management",
      description: "GoIntegra Facility Management Services",
    },
    {
      icon: Leaf,
      title: "Housekeeping",
      description: "Daily maintenance and housekeeping services",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="section-container py-4 flex items-center justify-between">
          <Link href="/">
            <a className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2">
              ← Back to Home
            </a>
          </Link>
          <h1 className="text-xl font-bold text-foreground">Amenities & Facilities</h1>
          <div className="w-32"></div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="section-container">
          <div className="max-w-4xl">
            <h2 className="section-title mb-4">World-Class Amenities</h2>
            <p className="section-subtitle mb-12">
              Comprehensive facilities designed for modern living and community engagement
            </p>

            {/* Recreation & Wellness */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
                <Waves className="w-8 h-8 text-blue-600" />
                Recreation & Wellness
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recreationAmenities.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <Icon className="w-6 h-6 text-blue-600 mt-1" />
                        </div>
                        <div>
                          <h4 className="font-bold text-foreground mb-2">{amenity.title}</h4>
                          <p className="text-sm text-muted-foreground">{amenity.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Infrastructure & Utilities */}
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
                <Zap className="w-8 h-8 text-blue-600" />
                Infrastructure & Utilities
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {infrastructureAmenities.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <Icon className="w-6 h-6 text-blue-600 mt-1" />
                        </div>
                        <div>
                          <h4 className="font-bold text-foreground mb-2">{amenity.title}</h4>
                          <p className="text-sm text-muted-foreground">{amenity.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="mt-16 bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-foreground mb-6">Amenities Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-3xl font-bold text-blue-600">12</p>
                  <p className="text-sm text-muted-foreground mt-2">Recreation Facilities</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">12</p>
                  <p className="text-sm text-muted-foreground mt-2">Infrastructure Features</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">24/7</p>
                  <p className="text-sm text-muted-foreground mt-2">Security & Monitoring</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">100%</p>
                  <p className="text-sm text-muted-foreground mt-2">Power Backup</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Amenities;
